import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import {
  AttributeSetUpInfoService,
  AttributeInfoRow,
  AttributeInfoLabel,
} from './attribute-set-up-info.service';

const VALID_LABELS: AttributeInfoLabel[] = ['Base Basket', 'Base Type', 'Base FDII', 'Tax Year/Scenario'];

@Component({
    selector: 'gtw-attribute-set-up-info',
    templateUrl: './attribute-set-up-info.component.html',
    styleUrls: ['./attribute-set-up-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AttributeSetUpInfoComponent implements OnInit, OnDestroy {
  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: any = {};
  filterParams: any = {};
  private globalParams: any = {};
  globalService: any;
  appService: any = { USER_SETTINGS: '', SERVER_CONFIG: '' };
  GENERAL_CONFIG: any;

  rowData: any = {};
  colData: any = {};
  label: AttributeInfoLabel | null = null;

  isLoading = true;
  title = '';
  baseData: AttributeInfoRow[] = [];

  private initComplete = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: AttributeSetUpInfoService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.initComplete = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = { ...header };
    this.appService.USER_SETTINGS = header.userSettingsData;
    this.appService.SERVER_CONFIG = header.serverConfigData;
    this.filterParams = normalizeFilterParams(activeScreen);

    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;
    this.colData = activeScreen?.data?.colData || activeScreen?.data?.col_data || {};

    this.extractLabelFromColData();

    const lbl = activeScreen?.data?.label || '';
    if (VALID_LABELS.includes(lbl as AttributeInfoLabel)) {
      this.label = lbl as AttributeInfoLabel;
      this.title = lbl;
    }

    this.globalParams = header.globalParams || {};
  }

  private extractLabelFromColData(): void {
    if (this.colData?.label) {
      const label = this.colData.label;
      if (VALID_LABELS.includes(label as AttributeInfoLabel)) {
        this.label = label as AttributeInfoLabel;
        this.title = label;
      }
    }
  }

  private init(): void {
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData?.TAG_KEY || !this.label) {
      return;
    }
    if (this.initComplete) {
      return;
    }
    this.initComplete = true;
    this.loadData();
  }

  private loadData(): void {
    if (!this.label) {
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const baseUrl = this.GENERAL_CONFIG.base_url;
    const tagKey = String(this.rowData.TAG_KEY || '');
    const issueId = String(this.rowData.ISSUE_ID || '');
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    const taxYear = String(this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '');
    const scenario = String(this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '');

    this.isLoading = true;
    this.cdr.detectChanges();

    this.subs.add(
      this.service.getBaseDataInfo(baseUrl, this.label, tagKey, issueId, ssoId, taxYear, scenario, this.globalParams, this.filterParams).subscribe({
        next: (data: AttributeInfoRow[]) => {
          this.baseData = data;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading attribute info:', err);
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      })
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
